<template>
  <v-dialog v-model="visible" max-width="480" @keydown.esc="close">
    <v-card>
      <v-card-title class="text-h6 font-weight-bold primary white--text">
        Mis datos bancarios
      </v-card-title>

      <v-card-text class="pt-6">
        <p class="subtitle-2 font-weight-bold mb-1">Banco</p>
        <v-select
          v-model="selectedBank"
          :items="banks"
          item-text="name"
          item-value="id"
          return-object
          outlined
          dense
          placeholder="Selecciona un banco"
          :loading="loading"
          :disabled="!isEditing || loading"
          @change="onBankChange"
        ></v-select>

        <p class="subtitle-2 font-weight-bold mb-1">Tipo de cuenta</p>
        <v-select
          v-model="form.accountType"
          :items="accountTypes"
          outlined
          dense
          placeholder="Selecciona un tipo de cuenta"
          :disabled="!isEditing || !selectedBank || loading"
        ></v-select>

        <p class="subtitle-2 font-weight-bold mb-1">Número de cuenta</p>
        <v-text-field
          v-model="form.accountNumber"
          outlined
          dense
          :disabled="!isEditing || loading"
          :placeholder="isEditing ? 'Ingresa el número de cuenta' : 'Sin número de cuenta registrado'"
        ></v-text-field>

        <p class="subtitle-2 font-weight-bold mb-1">CCI (Código de cuenta interbancario)</p>
        <v-text-field
          v-model="form.cci"
          outlined
          dense
          :disabled="!isEditing || loading"
          placeholder="Ingresa el CCI"
          :rules="[v => !v || v.length === 20 || 'Debe ingresar un número de CCI válido (20 dígitos)']"
        ></v-text-field>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn v-if="isEditing" text @click="cancelEdit">Cancelar</v-btn>
        <v-btn v-else text @click="close">Cerrar</v-btn>
        <v-btn
          v-if="isEditing && can('register-refund:update-bank-data')"
          color="primary"
          depressed
          :loading="saving"
          :disabled="loading"
          @click="save"
        >Guardar</v-btn>
        <v-btn
          v-else-if="can('register-refund:update-bank-data')"
          color="primary"
          depressed
          :disabled="loading"
          @click="startEdit"
        >Editar datos</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import BanksService from '../services/banks.service';
import { can } from '../utils/permissions';

export default {
  name: 'AddBankDataModal',
  props: {
    userId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      banks: [],
      selectedBank: null,
      accountTypes: [],
      existingId: null,
      // Modo lectura por defecto: los datos guardados no se pueden editar
      // hasta presionar "Editar datos".
      isEditing: false,
      // Snapshot del estado de solo lectura, para poder revertir al cancelar.
      readOnlySnapshot: null,
      form: {
        accountType: null,
        accountNumber: null,
        cci: null
      }
    };
  },
  methods: {
    can,
    async open() {
      this.resetForm();
      this.visible = true;
      this.loading = true;

      // 1. Obtener datos bancarios del usuario para saber su país y pre-llenar
      const bankDataRes = await BanksService.getUserBankData(this.userId);
      const bankData = bankDataRes.status === 'ok' ? bankDataRes.data : null;

      // 2. Detectar iso desde el país del usuario
      const iso = (bankData && bankData.isoCountry) || 'CL';

      // 3. Cargar bancos con el iso correcto
      const banksRes = await BanksService.getBanks(iso);
      if (banksRes.status === 'ok') {
        this.banks = banksRes.data;
      }

      this.loading = false;

      // 4. Pre-llenar si el usuario tiene datos bancarios
      if (bankData && bankData.bankId) {
        this.prefill(bankData);
      } else {
        // Sin datos previos no hay nada que leer: se abre directo en edición.
        this.isEditing = true;
      }
    },
    close() {
      this.visible = false;
    },
    resetForm() {
      this.selectedBank = null;
      this.accountTypes = [];
      this.existingId = null;
      this.isEditing = false;
      this.readOnlySnapshot = null;
      this.form = { accountType: null, accountNumber: null, cci: null };
    },
    prefill(data) {
      const match = this.banks.find(b => b.id === data.bankId);
      if (match) {
        this.selectedBank = match;
        this.accountTypes = match.accountTypesAccepted || [];
      }
      this.existingId = data.id || null;
      this.form.accountType = data.accountType || null;
      this.form.accountNumber = data.accountNumber ? String(data.accountNumber) : null;
      this.form.cci = data.cci || null;
      this.readOnlySnapshot = {
        bank: this.selectedBank,
        accountTypes: this.accountTypes,
        accountType: this.form.accountType,
        accountNumber: this.form.accountNumber,
        cci: this.form.cci
      };
    },
    // El panel de administración recibe los datos en claro (los necesita para
    // operar reembolsos), así que editar solo habilita los campos.
    startEdit() {
      this.isEditing = true;
    },
    cancelEdit() {
      const snapshot = this.readOnlySnapshot;
      if (!snapshot) {
        this.close();
        return;
      }
      this.selectedBank = snapshot.bank;
      this.accountTypes = snapshot.accountTypes;
      this.form = {
        accountType: snapshot.accountType,
        accountNumber: snapshot.accountNumber,
        cci: snapshot.cci
      };
      this.isEditing = false;
    },
    onBankChange(bank) {
      this.form.accountType = null;
      this.accountTypes = bank.accountTypesAccepted || [];
    },
    async save() {
      if (!this.isEditing) {
        return;
      }
      if (!this.selectedBank || !this.form.accountType || !this.form.accountNumber) {
        return;
      }
      // El backend rechaza cualquier valor que no sean dígitos.
      if (!/^\d{5,32}$/.test(String(this.form.accountNumber).trim())) {
        return;
      }
      if (this.form.cci && this.form.cci.length !== 20) {
        return;
      }
      this.saving = true;
      const payload = {
        accountNumber: this.form.accountNumber,
        accountType: this.form.accountType,
        cci: this.form.cci || undefined,
        id: this.existingId || undefined
      };
      const res = await BanksService.saveBankData(this.userId, this.selectedBank.id, payload);
      this.saving = false;
      if (res.status === 'ok') {
        this.$emit('saved', res.data);
        this.close();
      } else {
        this.$emit('error');
      }
    }
  }
};
</script>
